import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ReactComponent as IconCrossRound } from "../Images/icon_cross_round.svg";
import { ReactComponent as RightBracket } from "../Images/right_bracket.svg";
import LayoutModal from "../Layout/LayoutModal";
import "../Styles/component.css";
import "../Styles/fonts.css";
import "../Styles/global.css";
import { postJson } from "../Utils/apiUtils";
import {
  canSubmitAdminInfo,
  formatAdminPhoneInput,
  getAdminInfoSubmitErrorMessage,
  isValidAdminEmail,
  isValidAdminPhone,
  normalizeAdminInfoPayload,
} from "../Utils/adminInfoValidation";
import { showToast } from "../Utils/variables";

const SetAdminBox = ({ handleClickBack, handleClickX }) => {
  const navigate = useNavigate();
  const [adminInfoId, setAdminInfoId] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [department, setDepartment] = useState("");
  const [position, setPosition] = useState("");

  const [personalInfoChecked, setPersonalInfoChecked] = useState(false);
  const [marketingChecked, setMarketingChecked] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getAdminInfo = async () => {
    try {
      const res = await postJson(
        "/dashboard/company/getCompanyAdminInfo",
        {},
        navigate
      );

      if (res.code === 200) {
        const data = res.data;
        if (data) {
          setIsEditing(true);
          setAdminInfoId(data.companyAdminInfoId);
          setName(data.adminName);
          setEmail(data.adminEmail);
          setPhone(data.adminPhone);
          setDepartment(data.adminDepartment);
          setPosition(data.adminPosition);
        } else {
          setAdminInfoId(null);
          setIsEditing(false);
        }
      }
    } catch (error) {
      console.error("관리자 정보 가져오기 중 에러: ", error);
    }
  };

  const handleUpdateAdmin = async () => {
    if (isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      const body = normalizeAdminInfoPayload({
        adminInfoId,
        name,
        email,
        phone,
        department,
        position,
      });

      const res = await postJson(
        "/dashboard/company/updateCompanyAdminInfo",
        body,
        navigate
      );

      if (res.code === 200) {
        await getAdminInfo();
        showToast("관리자 정보가 저장되었습니다.", "success");
        handleClickBack();
      } else {
        showToast(getAdminInfoSubmitErrorMessage(res, { isEditing: true }), "error");
      }
    } catch (error) {
      console.error("관리자 정보 수정 중 에러: ", error);
      showToast("관리자 정보 수정 중 오류가 발생했습니다.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      const { companyAdminInfoId, ...body } = normalizeAdminInfoPayload({
        name,
        email,
        phone,
        department,
        position,
      });

      const res = await postJson(
        "/dashboard/company/createCompanyAdminInfo",
        body,
        navigate
      );

      if (res.code === 200) {
        await getAdminInfo();
        showToast("관리자 정보가 저장되었습니다.", "success");
        handleClickBack();
      } else {
        showToast(
          getAdminInfoSubmitErrorMessage(res, { isEditing: false }),
          "error"
        );
      }
    } catch (error) {
      console.error("관리자 정보 등록 중 에러: ", error);
      showToast("관리자 정보 저장 중 오류가 발생했습니다.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrivacyPolicyClick = () => {
    const newWindow = window.open("", "_blank", "width=800,height=600");
    newWindow.document.write(`
      <html>
        <head>
          <title>개인정보처리방침</title>
        </head>
        <body>
          <iframe 
            src="${process.env.PUBLIC_URL}/대시보드_개인정보처리방침.html" 
            width="100%" 
            height="100%" 
            style="border:none;"
          ></iframe>
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  const handleMarketingPolicyClick = () => {
    const newWindow = window.open("", "_blank", "width=500,height=300");
    newWindow.document.write(`
      <html>
        <head>
          <title>마케팅 활용 동의 및 광고 수신 동의</title>
        </head>
        <body>
          <h3>마케팅 활용 동의 및 광고 수신 동의</h1>
          <p>랭킹마라톤에서 제공하는 서비스와 관련된 신상품 소식, 이벤트 안내, 고객 혜택, 파트너스십 관련 안내 사항을 휴대전화나 이메일로 받아보실 수 있습니다.</p>
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  useEffect(() => {
    getAdminInfo();
  }, []);

  const isButtonEnabled = canSubmitAdminInfo({
    name,
    email,
    phone,
    isEditing,
    personalInfoChecked,
    marketingChecked,
  });
  const emailError =
    email && !isValidAdminEmail(email)
      ? "이메일 형식을 확인해주세요."
      : null;
  const phoneError =
    phone && !isValidAdminPhone(phone)
      ? "연락처는 숫자 기준 9~12자리로 입력해주세요."
      : null;
  const consentError =
    !isEditing &&
    !personalInfoChecked &&
    !marketingChecked &&
    name &&
    email &&
    phone
      ? "개인정보 수집 및 마케팅 정보 수신에 동의해주세요."
      : !isEditing && name && email && phone && !personalInfoChecked
        ? "개인정보 수집 및 이용에 동의해주세요."
        : !isEditing && name && email && phone && !marketingChecked
          ? "마케팅 정보 수신 이용에 동의해주세요."
          : null;

  return (
    <Box>
      <div className="hrz-wrapper">
        <RightBracket
          stroke="#b0b0b0"
          width="32px"
          height="32px"
          style={{ transform: "rotate(180deg)", cursor: "pointer" }}
          onClick={handleClickBack}
        />
        <p className="bold-text" style={{ fontSize: "24px" }}>
          관리자 정보 설정
        </p>
        <IconCrossRound
          style={{ marginLeft: "auto", cursor: "pointer" }}
          onClick={handleClickX}
        />
      </div>

      <p style={{ marginTop: "30px" }}>
        유효한 연락처 또는 이메일을 등록해주세요.
        <br />
        설정한 정보는 대시보드 이용 중 관리자와의 개별적인 컨텍이 필요할 경우에
        <br />만 이용됩니다. (ex. 환불)
      </p>

      <ScrollBox>
        <p>성함 *</p>
        <InputBox
          _value={name}
          onSetValue={(value) => setName(value)}
          placeholder="홍길동"
        />
        <p style={{ marginTop: "18px" }}>이메일 *</p>
        <InputBox
          _value={email}
          onSetValue={(value) => setEmail(value)}
          placeholder="123@Ranking.com"
          type="email"
          inputMode="email"
        />
        {emailError && <ErrorText>{emailError}</ErrorText>}
        <p style={{ marginTop: "18px" }}>연락처 *</p>
        <InputBox
          _value={phone}
          onSetValue={(value) => setPhone(formatAdminPhoneInput(value))}
          placeholder="01012345678"
          type="tel"
          inputMode="numeric"
          maxLength={13}
        />
        {phoneError && <ErrorText>{phoneError}</ErrorText>}
        <p style={{ marginTop: "18px" }}>부서</p>
        <InputBox
          _value={department}
          onSetValue={(value) => setDepartment(value)}
          placeholder="ex) 인사팀"
        />
        <p style={{ marginTop: "18px" }}>직책</p>
        <InputBox
          _value={position}
          onSetValue={(value) => setPosition(value)}
          placeholder="ex) 팀장"
        />

        {!isEditing && (
          <div style={{ marginTop: "30px" }}>
            <div className="hrz-wrapper">
              <input
                type="checkbox"
                style={{ width: "20px", height: "20px", cursor: "pointer" }}
                checked={personalInfoChecked}
                onChange={(e) => setPersonalInfoChecked(e.target.checked)}
              />
              <ConsentText>
                <span>[필수]&nbsp;</span>
                <ConsentLink onClick={handlePrivacyPolicyClick}>
                  랭킹마라톤 개인정보 수집 및 이용
                </ConsentLink>
                <span>&nbsp;동의</span>
              </ConsentText>
            </div>
            <div className="hrz-wrapper" style={{ marginTop: "10px" }}>
              <input
                type="checkbox"
                style={{ width: "20px", height: "20px", cursor: "pointer" }}
                checked={marketingChecked}
                onChange={(e) => setMarketingChecked(e.target.checked)}
              />
              <ConsentText>
                <span>[필수]&nbsp;</span>
                <ConsentLink onClick={handleMarketingPolicyClick}>
                  랭킹마라톤 마케팅 정보 수신 이용
                </ConsentLink>
                <span>&nbsp;동의</span>
              </ConsentText>
            </div>
            {consentError && <HelperText>{consentError}</HelperText>}
          </div>
        )}
      </ScrollBox>

      <div
        className="blueBtn-base"
        style={{
          width: "500px",
          height: "56px",
          borderRadius: "10px",
          marginTop: "40px",
          backgroundColor: isButtonEnabled && !isSubmitting ? "#1167ed" : "#b0b0b0",
          cursor: isButtonEnabled && !isSubmitting ? "pointer" : "default",
        }}
        onClick={
          isButtonEnabled && !isSubmitting
            ? () => {
                if (isEditing) {
                  handleUpdateAdmin();
                } else {
                  handleCreateAdmin();
                }
              }
            : null
        }
      >
        {isSubmitting ? "저장 중..." : "저장하기"}
      </div>
    </Box>
  );
};

const InputBox = ({
  _value,
  onSetValue,
  placeholder = "",
  type = "text",
  inputMode,
  maxLength,
}) => {
  return (
    <div
      className="textinputBox"
      style={{ marginTop: "14px", padding: "16px 14px" }}
    >
      <input
        type={type}
        inputMode={inputMode}
        maxLength={maxLength}
        placeholder={placeholder}
        style={{
          width: "478px",
          height: "21px",
          fontFamily: "notoSans-medium",
        }}
        value={_value}
        className="textinput"
        onChange={(e) => {
          onSetValue(e.target.value);
        }}
      />
    </div>
  );
};

const Box = styled.div`
  width: auto;
  height: auto;
  padding: 30px;
  border-radius: 20px;
  background-color: #ffffff;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
`;

const ErrorText = styled.p`
  margin-top: 8px;
  color: #f25c5c;
  font-size: 13px;
`;

const HelperText = styled.p`
  margin-top: 10px;
  color: #f25c5c;
  font-size: 13px;
`;

const ConsentText = styled.div`
  margin-left: 7px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const ConsentLink = styled.span`
  color: #1167ed;
  cursor: pointer;
  text-decoration: underline;
`;

const ScrollBox = styled.div`
  margin-top: 30px;
  width: 500px;
  height: 410px;
  overflow-y: scroll;
  padding: 3px;
  &::-webkit-scrollbar {
    width: 4px;
    z-index: 1;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #d9d9d9;
    border-radius: 20px;
  }
`;

const SetAdmin = ({ handleClickBack, handleClickX }) => {
  return (
    <LayoutModal
      Modal={
        <SetAdminBox
          handleClickBack={handleClickBack}
          handleClickX={handleClickX}
        />
      }
    />
  );
};

export default SetAdmin;
